import React, { useEffect, useMemo, useRef, useState } from "react";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FINANCE_V2_HOSTNAME,
  INTRANETHOSTNAME,
  INVOICE_TRACKING_HOSTNAME,
} from "../../../../common-utilities/utils/end-points";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import {
  createPayloadMapper,
  getAdvanceAmount,
  hasUnmappedPrItems,
  mapPoFileUploadResponse,
} from "./helper";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import {
  Autocomplete,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import "./index.scss";
import { AddCircle, ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AddItemModal from "./add-item-modal";
import {
  convertToCamelCaseFromUnderScore,
  formatDate,
} from "../../../../common-utilities/utils";
import { toast } from "react-toastify";
import { getOfflineData } from "../../../utils/offline-services";
import { isStoreRole } from "../../../utils/common-utils";
import FileUpload from "../../../../common-utilities/core/file-uploader";
import { DATE_FORMATS } from "../../../../common-utilities/utils/constants";
import DownloadTemplateButton from "../../master-data/common-components/upload-template";
import { uploadTemplates } from "../../master-data/constants";
import {
  getPOAmount,
  getPOLineItemAmount,
  createPOavailableBudgetAmount,
  availableBudgetAmount,
  getPOAmountWithoutTax,
  getExtraChargesTotal,
  PO_ITEM_MODEL,
  normalizeLocationId,
  getAllocationForItem,
  computeAllocationAvailable,
} from "../helper";
import ExtraChargesSection, {
  hasIncompleteChargeRow,
  EMPTY_CHARGE_ROW,
} from "../common-components/extra-charges-section";
import {
  fetchRateContractStatus,
  getRateContractRequiredItemIds,
} from "../rate-contract-utils";
import { getCurrencyList } from "../../master-data/foreign-currency/apis";
import CustomModal from "../../../../common-utilities/core/modal";
import POInfo from "./po-info";
import { HOME_TABS, getHomeTabUrl, getPoListSubIndex } from "../../../utils/home-tabs";
import { setProperty } from "../../../../common-utilities/utils/misc";
import {
  getUploadAttachmentQueryParams,
  uploadAttachmentTypes,
} from "../../../utils/attachments";

const DEFAULT_COMPANY =
  "Culinary Brands India Private Limited (Formerly known as Eversub India Private Limited)";

const resolvePRSiteId = (site) =>
  site && typeof site === "object" ? site.id ?? site._id : site;

const CreatePO = ({}) => {
  const navigate = useNavigate();
  const userDetails = getOfflineData("user");
  const { department } = userDetails;
  const { budgetId } = useParams();
  const [searchParams] = useSearchParams();
  const poType = searchParams.get("poType");
  const lineItemIds = searchParams.get("lineItemIds");
  const prId = searchParams.get("prId");
  const isPRLinked = !!prId;
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [formDetails, setFormDetails] = useState({});
  const [poItems, setPoItems] = useState({});
  const [vendors, setVendors] = useState([]);
  const [budgetDetails, setBudgetDetails] = useState({});
  const [departmentList, setDepartmentList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [autocompleteInputValues, setAutocompleteInputValues] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [customSites, setCustomSites] = useState([]);
  const [isItemsUploaded, setItemsUploaded] = useState(false);
  const [createdPoDetails, setCreatedPoDetails] = useState(null);
  const [config, setConfig] = useState({
    isItemDeleted: false,
  });
  const [prDetails, setPrDetails] = useState(null);
  const isSingleStorePRLinked = isPRLinked && prDetails?.prScope === "SINGLE_STORE";
  const isSelfPRLinked =
    isPRLinked &&
    prDetails?.prScope === "HEAD_OFFICE" &&
    prDetails?.storeSelectionType === "SELF";
  // Both scopes resolve to exactly one valid delivery store for the PR, so it's
  // prefilled and locked rather than left as a free choice among filtered options.
  const isDeliveryLocationLocked = isSingleStorePRLinked || isSelfPRLinked;
  const prPrefilledRef = useRef(false);
  const [prQuantityMap, setPrQuantityMap] = useState({});
  const [prVendorOptions, setPrVendorOptions] = useState([]);
  const [prVendorsLoading, setPrVendorsLoading] = useState(false);
  const [prItemFilterLoading, setPrItemFilterLoading] = useState(false);
  // Keyed by itemId -> { active, rate, taxPercentage }. Shared between the
  // independent (non-PR) and PR-linked flows -- only one is active at a time.
  const [rateContractStatusMap, setRateContractStatusMap] = useState({});
  const [rateContractRevalidating, setRateContractRevalidating] = useState(false);
  const [subcategoryMap, setSubcategoryMap] = useState({});
  const fetchedCategoryIdsRef = useRef(new Set());
  const [extraCharges, setExtraCharges] = useState([{ ...EMPTY_CHARGE_ROW }]);

  const { vendor = {} } = formDetails;

  let selectedLineItemIds = [];

  if (lineItemIds) {
    selectedLineItemIds = lineItemIds.split(",");
  }

  const { site = "", siteName = "", lineItems = [], siteCode } = budgetDetails;

  // PR-linked POs display the currently selected Delivery Location's own site --
  // budgetDetails.site is only ever an arbitrary pooled STORE's budget for HEAD_OFFICE
  // PRs, not the store the PO is actually for. For multi-store HO PRs this stays blank
  // until the user picks a Delivery Location, then updates immediately to that store.
  // Independent (non-PR) POs keep showing the selected budget's own site, unchanged.
  const displaySiteName = isPRLinked
    ? formDetails.deliveryLocation?.name || ""
    : siteName;
  const displaySiteCode = isPRLinked
    ? formDetails.deliveryLocation?.code || ""
    : siteCode;

  useEffect(() => {
    loadData();
    loadCategoryDetails();
    getCurrencyList(setCurrencyOptions, setFormDetails);
  }, []);

  // prId path: fetch the approved PR
  useEffect(() => {
    if (!prId) return;
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}/${prId}`
    )
      .then((data) => {
        if (!data.message) setPrDetails(data);
      })
      .catch(() => {
        toast.error(
          "Could not load the linked Purchase Requisition's store details. Delivery Location options will stay restricted until this succeeds."
        );
      });
  }, [prId]);

  // prId path: pre-fill line items as soon as PR data arrives, and re-derive whenever the
  // selected Delivery Location changes -- a HEAD_OFFICE PR's allocations are per-store, so
  // the balance/default quantity shown must always match whichever store is selected right
  // now, not a pooled/PR-wide number. Falls back to the PR's own site (resolves the single
  // allocation immediately for SINGLE_STORE/HEAD_OFFICE-SELF); for a multi-store PR with no
  // store picked yet, no allocation matches and items default to a balance of 0 rather than
  // a guessed/rebuilt number.
  useEffect(() => {
    if (!prId || !prDetails) return;
    const storeId =
      normalizeLocationId(formDetails.deliveryLocation) ?? normalizeLocationId(prDetails.site);
    const newPoItems = {};
    const newPrQuantityMap = {};
    (prDetails.items || []).forEach((prItem) => {
      const allocation = getAllocationForItem(prItem, storeId);
      const remaining = allocation ? computeAllocationAvailable(allocation) : 0;
      const liId = prItem.lineItem?.id || String(prItem.lineItem ?? "");
      if (!liId) return;
      if (!newPoItems[liId]) {
        newPoItems[liId] = [];
        newPrQuantityMap[liId] = [];
      }
      const itemObj =
        prItem.item && typeof prItem.item === "object"
          ? prItem.item
          : { id: String(prItem.item ?? ""), name: prItem.itemName || "" };
      // Resolve against the specific categoryMappings pair the PR line actually stamped
      // (matched by prItem.subCategory, the PR's own resolved id) rather than
      // prItem.item.category/subCategory -- that's just the Item Master's deprecated
      // single mirror, which for a multi-mapping item may not be the pair this PR line
      // was actually raised against. Mirrors the same lookup create-pr's add-item-modal
      // uses to avoid this exact trap.
      // prItem.category/subCategory are documented as plain numeric ids, but PurchaseRequest
      // is always fetched with items.category/items.subCategory populated server-side --
      // extract the id defensively (same as helper.js's getId) instead of assuming a
      // primitive, so a populated object here is never blindly String()'d into the literal
      // text "[object Object]" (which then fails the create-PO payload's "must be a number"
      // validation).
      const prSubCategoryId = prItem.subCategory?.id ?? prItem.subCategory?._id ?? prItem.subCategory;
      const prCategoryId = prItem.category?.id ?? prItem.category?._id ?? prItem.category;
      const matchingMapping = (prItem.item?.categoryMappings || []).find(
        (m) => String(m.subCategory?._id ?? m.subCategory?.id ?? m.subCategory) === String(prSubCategoryId)
      );
      const subCatObj =
        matchingMapping?.subCategory && typeof matchingMapping.subCategory === "object"
          ? matchingMapping.subCategory
          : { id: String(prSubCategoryId ?? ""), name: "" };
      const catObj =
        matchingMapping?.category && typeof matchingMapping.category === "object"
          ? matchingMapping.category
          : { id: String(prCategoryId ?? ""), name: "" };
      if (catObj?.id) loadSubcategoriesForCategory(catObj.id);
      newPoItems[liId].push({
        ...PO_ITEM_MODEL,
        id: Math.random(),
        item: itemObj,
        subCategory: subCatObj,
        category: catObj,
        quantity: remaining,
        remainingQuantity: remaining,
        rate: prItem.estimatedRate || 0,
        taxRate: prItem.taxRate || 0,
        taxPercentage: prItem.taxRate || 0,
        // PR's own estimate, kept separate from rate/taxRate (which the user or a rate
        // contract may change) -- feeds prBudgetOffsetByLineItem below.
        prEstimatedRate: prItem.estimatedRate || 0,
        prTaxRate: prItem.taxRate || 0,
        name: prItem.itemName || itemObj.name || "",
        uom: prItem.uom || "",
        hsnCode: prItem.hsnCode || "",
        itemId: itemObj.id || "",
        lineItemId: liId,
        lineItemName: prItem.lineItem?.name || "",
        specification: prItem.description || "",
        mainCategoryName: catObj.name || "",
        mainCategoryId: String(catObj.id ?? ""),
        subcategoryName: subCatObj.name || "",
        subcategoryId: String(subCatObj.id ?? ""),
      });
      newPrQuantityMap[liId].push(remaining);
    });
    setPoItems(newPoItems);
    setPrQuantityMap(newPrQuantityMap);
  }, [prDetails, formDetails.deliveryLocation]);

  // prId path: pre-fill paymentTerms and poType once PR details are loaded.
  // Department is intentionally NOT prefilled from the PR here -- approval routing (and this
  // field) follows the PO creator's own department, not the PR creator's, since the PR
  // creator's involvement ends once the PR is approved and they may belong to a different
  // department entirely (or none, for a store-originated PR). It already defaults to the
  // logged-in user's (the PO creator's) own department from the department-list load above.
  useEffect(() => {
    if (!prId || !prDetails || prPrefilledRef.current) return;
    prPrefilledRef.current = true;
    const updates = {};
    if (prDetails.paymentTerms) {
      updates.paymentTerms = prDetails.paymentTerms;
    }
    if (prDetails.prType) {
      updates.poType =
        prDetails.prType === "CAPEX"
          ? "capex_po"
          : prDetails.prType === "OPEX"
          ? "opex_po"
          : prDetails.prType.toLowerCase();
    }
    if (Object.keys(updates).length > 0) {
      setFormDetails((prev) => ({ ...prev, ...updates }));
    }
  }, [prDetails]);

  // prId path: build the list of vendors that have an Approved mapping for at least one item on this PR.
  // Unlike the item-balance effect below, this one never falls back to prDetails.site -- a
  // multi-store PR's real target stores don't include the PR's own requesting site, so no
  // vendor can be considered "eligible" until the user has actually picked a Delivery Location.
  useEffect(() => {
    if (!prId || !prDetails || vendors.length === 0) return;
    const storeId = normalizeLocationId(formDetails.deliveryLocation);
    if (!storeId) {
      setPrVendorOptions([]);
      return;
    }
    const itemIds = [
      ...new Set(
        (prDetails.items || [])
          .filter((pi) => {
            const allocation = getAllocationForItem(pi, storeId);
            return (allocation ? computeAllocationAvailable(allocation) : 0) > 0;
          })
          .map((pi) =>
            pi.item && typeof pi.item === "object"
              ? pi.item.id
              : String(pi.item ?? "")
          )
          .filter(Boolean)
      ),
    ];
    if (itemIds.length === 0) {
      setPrVendorOptions([]);
      return;
    }
    setPrVendorsLoading(true);
    Promise.all(
      itemIds.map((itemId) =>
        invokeApi(
          HTTP_METHODS.GET,
          `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.VENDOR_ITEM_MAPPING}`,
          null,
          {
            item: itemId,
            page: 1,
            limit: 1000,
            ...(formDetails.poDate ? { date: formDetails.poDate } : {}),
          }
        ).catch(() => null)
      )
    ).then((responses) => {
      setPrVendorsLoading(false);
      const vendorCodes = new Set();
      responses.forEach((res) => {
        if (!res || res.message) return;
        const results = res.results || (Array.isArray(res) ? res : []);
        results.forEach((m) => {
          if (m.vendorCode && m.status === "approved") vendorCodes.add(m.vendorCode);
        });
      });
      setPrVendorOptions(vendors.filter((v) => vendorCodes.has(v.code)));
    });
  }, [prId, prDetails, vendors, formDetails.deliveryLocation]);

  // single-site PR path (SINGLE_STORE, or HEAD_OFFICE/SELF): auto-fill and lock Delivery
  // Location to the PR's own site. prDetails.site is the PR's populated Location ref
  // ({ id, name, ... }) here, not a plain id -- resolvePRSiteId() normalizes it so this
  // never falls back to comparing an object against a string.
  useEffect(() => {
    if (!isDeliveryLocationLocked) return;
    if (formDetails.deliveryLocation) return;
    if (!customSites.length || !prDetails?.site) return;
    const prSiteId = resolvePRSiteId(prDetails.site);
    const matched = customSites.find((s) => String(s.id) === String(prSiteId));
    if (!matched) return;
    setFormDetails((prev) => ({ ...prev, deliveryLocation: matched }));
    setAutocompleteInputValues((prev) => ({
      ...prev,
      deliveryLocation: matched.restaurantName,
    }));
  }, [isDeliveryLocationLocked, customSites, prDetails, formDetails.deliveryLocation]);

  // prId path: Billing Location defaults to the PR's requesting HO/site location,
  // resolved from prDetails.site -- independent of whichever store is picked as the
  // multi-store PR's Delivery Location. Only fills when unset so the user can still
  // pick any other billing location afterwards. Applied at most once -- otherwise clearing
  // the field to search (which unsets the id) immediately re-filled it with the PR's location.
  const billingDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (billingDefaultAppliedRef.current) return;
    if (!isPRLinked) return;
    if (!customSites.length || !prDetails?.site) return;
    if (formDetails.billingLocation?.id) {
      billingDefaultAppliedRef.current = true;
      return;
    }
    const prSiteId = resolvePRSiteId(prDetails.site);
    const matched = customSites.find((s) => String(s.id) === String(prSiteId));
    if (!matched) return;
    billingDefaultAppliedRef.current = true;
    setFormDetails((prev) => ({ ...prev, billingLocation: matched }));
    setAutocompleteInputValues((prev) => ({
      ...prev,
      billingLocation: matched.restaurantName,
    }));
  }, [isPRLinked, customSites, prDetails, formDetails.billingLocation]);

  // multi-store PR path (non-locked): if the current selection falls outside the PR's
  // valid store list -- e.g. it was picked before prDetails narrowed the options, or the
  // PR data was still loading -- clear it instead of letting a stale, out-of-scope
  // delivery location reach the payload.
  useEffect(() => {
    if (!prId || isDeliveryLocationLocked) return;
    if (!formDetails.deliveryLocation) return;
    const validIds = getDeliveryAddressList().map((s) => String(s.id));
    if (validIds.includes(String(formDetails.deliveryLocation.id))) return;
    setFormDetails((prev) => ({ ...prev, deliveryLocation: null }));
    setAutocompleteInputValues((prev) => ({ ...prev, deliveryLocation: "" }));
  }, [prId, isDeliveryLocationLocked, prDetails, customSites, formDetails.deliveryLocation]);

  // prId path: a vendor is only ever verified eligible against whichever store was selected
  // at the time (see the vendor-eligibility effect above) -- if the Delivery Location is then
  // changed to a different store, that vendor's eligibility no longer holds, so clear it
  // rather than let stale items render under an unverified vendor.
  const prevDeliveryLocationIdRef = useRef(undefined);
  useEffect(() => {
    if (!prId) return;
    const currentId = formDetails.deliveryLocation?.id ?? null;
    const prevId = prevDeliveryLocationIdRef.current;
    prevDeliveryLocationIdRef.current = currentId;
    if (prevId !== undefined && prevId !== currentId && formDetails.vendor) {
      handlePrVendorSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prId, formDetails.deliveryLocation?.id]);

  // manual (non-PR) path: whenever the vendor changes, revalidate every rate-contract-required
  // row against the new vendor. Rows that are no longer covered by an active contract are
  // removed outright (independent-creation rows are freshly selectable, so nothing "already
  // associated" is being hidden); rows that are still/now valid get their rate refreshed.
  useEffect(() => {
    if (prId) return;
    const vendorCode = formDetails.vendor?.code;
    if (!vendorCode) return;
    const requiredItemIds = getRateContractRequiredItemIds(poItems);
    if (requiredItemIds.length === 0) return;
    setRateContractRevalidating(true);
    Promise.all(
      requiredItemIds.map((itemId) =>
        fetchRateContractStatus(vendorCode, itemId, formDetails.poDate).then((status) => ({
          itemId,
          status,
        }))
      )
    ).then((results) => {
      const map = {};
      const invalidIds = new Set();
      results.forEach(({ itemId, status }) => {
        map[itemId] = status;
        if (!status.active) invalidIds.add(itemId);
      });
      setRateContractStatusMap((prev) => ({ ...prev, ...map }));
      // Recompute against the LATEST poItems at apply-time (functional update), not the
      // snapshot captured when this effect started -- otherwise any row added/edited while
      // the fetches above were in flight would be silently reverted here.
      const removedNames = [];
      setPoItems((prevPoItems) => {
        const nextPoItems = {};
        Object.keys(prevPoItems).forEach((liId) => {
          nextPoItems[liId] = (prevPoItems[liId] || [])
            .filter((poItem) => {
              if (poItem.itemId && invalidIds.has(poItem.itemId)) {
                removedNames.push(poItem.name || poItem.item?.name || "Item");
                return false;
              }
              return true;
            })
            .map((poItem) => {
              const status = poItem.itemId ? map[poItem.itemId] : undefined;
              if (!status?.active) return poItem;
              return {
                ...poItem,
                rate: status.rate,
                taxRate: status.taxPercentage,
                taxPercentage: status.taxPercentage,
                refRate: status.rate,
              };
            });
        });
        return nextPoItems;
      });
      if (removedNames.length > 0) {
        toast.warning(
          `Removed ${removedNames.join(", ")} — no active rate contract for the selected vendor.`,
          { autoClose: 3500 }
        );
      }
      setRateContractRevalidating(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDetails.vendor?.code, prId]);

  // manual (non-PR) path: pre-warm the subcategory list for each selected line item's category
  useEffect(() => {
    if (prId) return;
    (lineItems || []).forEach((li) => {
      if (selectedLineItemIds.includes(li.id) && li.category?.id) {
        loadSubcategoriesForCategory(li.category.id);
      }
    });
  }, [lineItems, lineItemIds]);

  const loadData = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGETS}`,
      null,
      {
        page: 1,
        limit: 1000,
        status: "approved",
        _id: budgetId,
        populateLocation: true,
      }
    )
      .then((response) => {
        if (response.message) {
        } else if (response.results.length > 0) {
          const site = response.results[0]?.site || {};
          setBudgetDetails({
            ...response.results[0],
            site: site.id,
            siteName: site.name,
            siteCode: site.code,
          });

          loadLocations((response.results[0] || {}).site);
          // budgetDetails.site is only ever an arbitrary pooled STORE's budget for
          // HEAD_OFFICE-scoped PRs (self or multi-store), never the HO's own location
          // nor any store the PR actually selected for delivery -- PR-linked POs derive
          // Delivery/Billing Location from prDetails instead (see the dedicated effects
          // below). Independent (non-PR) POs keep defaulting both to the budget's site.
          if (!isPRLinked && Object.keys(site).length) {
            const locationData = {
              id: site.id,
              restaurantName:
                site.storeType === "COCO BUILT"
                  ? `${site.code} | ${site.name} | ${site.address}`
                  : `${site.name} | ${site.address}`,
            };
            const locationName =
              site.storeType === "COCO BUILT"
                ? `${site.code} | ${site.name} | ${site.address}`
                : `${site.name} | ${site.address}`;
            setProperty(
              ["billingLocation", "deliveryLocation"],
              [locationData, locationData],
              setFormDetails
            );
            setProperty(
              ["billingLocation", "deliveryLocation"],
              [locationName, locationName],
              setAutocompleteInputValues
            );
          }
          const { lineItems = [] } = response.results[0];
          lineItems.forEach((lineItem) => {
            if (selectedLineItemIds.includes(lineItem.id)) {
              onRowAdd(lineItem);
              onRowAdd(lineItem);
            }
          });
        }
      })
      .catch((err) => {});
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${FINANCE_REST_URLS.DEPARTMENTS}`,
      null,
      {
        page: 1,
        limit: 1000,
      }
    )
      .then((response) => {
        if (response.message) {
        } else {
          setFormDetails((prevDetails) => ({
            ...prevDetails,
            department: isStoreRole()
              ? {}
              : response.find((r) => r._id === department) || {},
            poDate: formatDate(new Date(), DATE_FORMATS["YYYY-MM-DD"]),
            forex: 1,
          }));
          setDepartmentList(response || []);
        }
      })
      .catch((err) => {});

    invokeApi(
      HTTP_METHODS.GET,
      `${INVOICE_TRACKING_HOSTNAME}${FINANCE_REST_URLS.VENDOR_LIST}`,
      null,
      {
        page: 1,
        limit: 1000,
        additionalFields: "paymentTerms",
      }
    )
      .then((response) => {
        if (response.message) {
        } else {
          setVendors(response || []);
        }
      })
      .catch((err) => {});
  };

  const loadLocations = (poSite) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.LOCATIONS}`,
      null,
      {
        page: 1,
        // limit: 1000,
        limit: 1200,
      }
    )
      .then((response) => {
        if (response.message) {
        } else if (response && response.results.length > 0) {
          setCustomSites(
            response.results.map((r) => {
              return {
                id: r.id,
                name: r.name,
                code: r.code,
                restaurantName:
                  r.storeType === "COCO BUILT"
                    ? `${r.code} | ${r.name} | ${r.address}`
                    : `${r.name} | ${r.address}`,
              };
            }) || []
          );
        }
      })
      .catch((err) => {});
  };

  const loadSubcategoriesForCategory = (categoryId) => {
    const key = String(categoryId || "");
    if (!key || fetchedCategoryIdsRef.current.has(key)) return;
    fetchedCategoryIdsRef.current.add(key);
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.SUBCATEGORY}`,
      null,
      { page: 1, category: key, limit: 1000 }
    )
      .then((response) => {
        if (response.message) {
          fetchedCategoryIdsRef.current.delete(key);
          return;
        }
        setSubcategoryMap((prev) => ({ ...prev, [key]: response.results || [] }));
      })
      .catch(() => {
        fetchedCategoryIdsRef.current.delete(key);
      });
  };

  const loadCategoryDetails = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.COMPANY}`,
      null,
      {
        page: 1,
        limit: 1000,
      }
    )
      .then((response) => {
        if (response.message) {
        } else {
          setCompanyList(response.results || []);
          let selectedCompany =
            response.results.find((r) => r.name === DEFAULT_COMPANY) || {};
          setProperty(
            "company",
            selectedCompany.name,
            setAutocompleteInputValues
          );
          setProperty("company", selectedCompany, setFormDetails);
        }
      })
      .catch((err) => {});
  };

  const onSave = (params) => {
    // Defense in depth against isDisabled() somehow being stale/bypassed (see the backend
    // check on the same rule for the authoritative enforcement): never submit a PR-linked
    // PO while a line item has no active vendor mapping for the selected vendor.
    if (prId && hasUnmappedPrItems(prVisiblePoItems)) {
      const unmappedItem = Object.values(prVisiblePoItems || {})
        .flat()
        .find((poItem) => poItem?.rateContractMissing);
      toast.error(
        `No active rate contract for ${
          unmappedItem?.name || unmappedItem?.item?.name || "an item"
        }. Remove it before creating the PO.`
      );
      return;
    }

    if (!prId) {
      const prRequiredItem = Object.values(poItems || {})
        .flat()
        .find((poItem) => poItem?.itemId && poItem?.item?.prRequired);
      if (prRequiredItem) {
        toast.error(
          "PR is required for this item. Please create a PR before creating a PO."
        );
        return;
      }
    }

    if (
      !prId &&
      formDetails?.department?.isRateContractEnforced &&
      formDetails?.vendor?.code &&
      !isItemsUploaded
    ) {
      const allPoItems = Object.values(poItems || {}).flat();
      const invalidMappingItem = allPoItems.find(
        (item) => item?.itemId && !item?.item?.rateContractRequired && !item?.hasApprovedVendorMapping
      );
      if (invalidMappingItem) {
        const itemName =
          invalidMappingItem?.name ||
          invalidMappingItem?.item?.name ||
          "selected item";
        toast.warning(`Vendor mapping not present for ${itemName}.`, {
          autoClose: 3000,
        });
        return;
      }
    }

    if (rateContractRevalidating || prItemFilterLoading) {
      toast.warning("Still checking rate contracts for the selected vendor. Please wait.", {
        autoClose: 2500,
      });
      return;
    }

    let payload = createPayloadMapper(
      formDetails,
      prId ? prVisiblePoItems : poItems,
      budgetDetails,
      formDetails.poType || poType,
      attachments,
      prId,
      extraCharges
    );

    payload.purchaseRequestId = prId ? Number(prId) : null;

    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PO}`,
      payload,
      params
    )
      .then((response) => {
        if (response.code) {
          toast.error(response.message || "Failed Create purchase order ");
        } else {
          setCreatedPoDetails(response);
        }
      })
      .catch((err) => {});
  };

  const inputChange = (event) => {
    setFormDetails({ ...formDetails, [event.target.name]: event.target.value });
  };

  const onDropDownChange = (key, value) => {
    let additionalDetails = {};
    switch (key) {
      case "vendor":
        additionalDetails.paymentTerms = `${value?.paymentTerms || ""}`;
        break;
      default:
        break;
    }

    setFormDetails({ ...formDetails, [key]: value, ...additionalDetails });
  };

  const updateItem = (item, index) => {
    poItems[item.lineItemId][index] = {
      ...poItems[index],
      ...item,
    };
    setPoItems({ ...poItems });
  };

  const onDeleteRow = (id, index) => {
    setProperty("isItemDeleted", true, setConfig);
    poItems[id].splice(index, 1);
    setPoItems({ ...poItems });
    if (prId && prQuantityMap[id]) {
      prQuantityMap[id].splice(index, 1);
      setPrQuantityMap({ ...prQuantityMap });
    }
    setTimeout(() => {
      setProperty("isItemDeleted", false, setConfig);
    }, 200);
  };

  const onRowAdd = (lineItem) => {
    let items = poItems[lineItem.id] || [];
    poItems[lineItem.id] = [...items, PO_ITEM_MODEL];
    setPoItems({ ...poItems });
  };

  // prId path: select vendor → set form vendor + resolve rate-contract status for every
  // rateContractRequired item on this PR. Non-required items are unaffected — they always
  // stay visible/editable off the PR-approved rate, with no vendor-mapping requirement.
  const handlePrVendorSelect = (vendor) => {
    if (!vendor) {
      onDropDownChange("vendor", null);
      setRateContractStatusMap({});
      setPrItemFilterLoading(false);
      return;
    }
    onDropDownChange("vendor", vendor);
    setAutocompleteInputValues((prev) => ({
      ...prev,
      vendor: `${vendor.code} - ${vendor.name}`,
    }));
    const requiredItemIds = getRateContractRequiredItemIds(poItems);
    if (requiredItemIds.length === 0) {
      setRateContractStatusMap({});
      setPrItemFilterLoading(false);
      return;
    }
    setPrItemFilterLoading(true);
    Promise.all(
      requiredItemIds.map((itemId) =>
        fetchRateContractStatus(vendor.code, itemId, formDetails.poDate).then((status) => ({
          itemId,
          status,
        }))
      )
    ).then((results) => {
      const map = {};
      results.forEach(({ itemId, status }) => {
        map[itemId] = status;
      });
      setRateContractStatusMap(map);
      setPrItemFilterLoading(false);
    });
  };

  // prId path: delete a row by unique item id (safe across vendor-filtered views)
  const onDeletePrRow = (lineItemId, itemUniqueId) => {
    setProperty("isItemDeleted", true, setConfig);
    poItems[lineItemId] = (poItems[lineItemId] || []).filter(
      (item) => item.id !== itemUniqueId
    );
    setPoItems({ ...poItems });
    setTimeout(() => setProperty("isItemDeleted", false, setConfig), 200);
  };

  // prId path: update a row by unique item id (safe across vendor-filtered views)
  const updatePrItem = (updatedItem, lineItemId, itemUniqueId) => {
    const items = poItems[lineItemId] || [];
    const idx = items.findIndex((item) => item.id === itemUniqueId);
    if (idx >= 0) {
      items[idx] = { ...items[idx], ...updatedItem };
      setPoItems({ ...poItems });
    }
  };

  const getAddressList = () => {
    if (customSites.length > 0) {
      return customSites;
    } else {
      return [];
    }
  };

  const getDeliveryAddressList = () => {
    const addressList = getAddressList();
    if (!prId) return addressList;
    // PR-linked but the PR's own store data hasn't loaded yet -- fail closed (no options)
    // rather than briefly offering every location in the system.
    if (!prDetails) return [];

    const prStoreIds = (
      prDetails.selectedSites?.length > 0
        ? prDetails.selectedSites
        : [resolvePRSiteId(prDetails.site)]
    )
      .filter(Boolean)
      .map(String);

    const scoped = addressList.filter((s) => prStoreIds.includes(String(s.id)));
    // SINGLE_STORE/HEAD_OFFICE-SELF always resolve to their one prefilled-and-locked store
    // regardless of remaining balance. A multi-store PR's Delivery Location choice, though,
    // should only ever offer stores that still have something left to order against.
    if (isDeliveryLocationLocked) return scoped;
    return scoped.filter((s) => storeHasAvailableAllocation(s.id));
  };

  // Whether any item on this PR still has unconsumed (requested - reserved - converted -
  // short-closed) allocation quantity for the given store -- the gate for whether that store
  // is worth offering as a multi-store PR's Delivery Location at all.
  const storeHasAvailableAllocation = (storeId) =>
    (prDetails?.items || []).some((prItem) => {
      const allocation = getAllocationForItem(prItem, storeId);
      return allocation && computeAllocationAvailable(allocation) > 0;
    });

  const onLineItemsUpload = (response) => {
    const { data } = response;
    setPoItems(mapPoFileUploadResponse(data));
    setItemsUploaded(true);
  };

  const onDelete = (index) => {
    attachments.splice(index, 1);
    setAttachments([...attachments]);
  };

  // prId path: PR items whose balance is already fully allocated to a prior PO against this
  // same PR are hidden entirely (remainingQuantity, snapshotted from the PR's per-store
  // allocation when this screen loaded -- see the prId effect above). Previously every PR item
  // stayed visible regardless of balance, so re-opening "Create PO" against a
  // partially-converted PR re-showed already-fully-converted items alongside the genuinely
  // pending ones. See po-pr-short skill item #2. A line item group with nothing left pending
  // is dropped entirely rather than rendered as an empty accordion.
  // Items flagged rateContractRequired get their rate/tax/refRate recalculated from the
  // selected vendor's active rate contract when one exists, and are flagged
  // rateContractMissing when it doesn't (row stays visible but is disabled + blocks submit).
  // Non-required items pass through untouched — they keep the PR-approved rate regardless of
  // vendor mapping presence.
  const prVisiblePoItems = useMemo(() => {
    const out = {};
    Object.entries(poItems).forEach(([liId, items]) => {
      const pendingItems = items.filter((item) => Number(item.remainingQuantity) > 0);
      if (!pendingItems.length) return;
      out[liId] = pendingItems.map((item) => {
        if (!item.item?.rateContractRequired) {
          return { ...item, rateContractMissing: false };
        }
        const status = rateContractStatusMap[item.itemId];
        if (status?.active) {
          return {
            ...item,
            rate: status.rate,
            taxRate: status.taxPercentage,
            taxPercentage: status.taxPercentage,
            refRate: status.rate,
            item: { ...(item.item || {}), price: status.rate },
            rateContractMissing: false,
          };
        }
        return { ...item, rateContractMissing: true };
      });
    });
    return out;
  }, [poItems, rateContractStatusMap]);

  // prId path: this PR's own estimate for the quantity being converted, per line item.
  // The PR already reserved it out of availableAmount, so it is credited back before
  // comparing -- mirrors the backend's budgetOffset (computeActualAndEstimatedByLineItem).
  const prBudgetOffsetByLineItem = useMemo(() => {
    const out = {};
    if (!prId) return out;
    Object.entries(prVisiblePoItems).forEach(([liId, items]) => {
      out[liId] = items.reduce(
        (sum, item) =>
          sum +
          (Number(item.quantity) || 0) *
            (Number(item.prEstimatedRate) || 0) *
            (1 + (Number(item.prTaxRate) || 0) / 100),
        0
      );
    });
    return out;
  }, [prId, prVisiblePoItems]);

  // Direct POs keep the raw lineItems untouched.
  const budgetLineItems = useMemo(() => {
    if (!prId) return lineItems;
    return (lineItems || []).map((li) =>
      prBudgetOffsetByLineItem[li.id]
        ? {
            ...li,
            availableAmount:
              (Number(li.availableAmount) || 0) + prBudgetOffsetByLineItem[li.id],
          }
        : li
    );
  }, [prId, lineItems, prBudgetOffsetByLineItem]);

  const isDisabled = () => {
    const effectivePoItems = prId ? prVisiblePoItems : poItems;
    let isAmtValid = true;
    budgetLineItems?.map((lineItem) => {
      if (
        lineItem?.status == "approved" &&
        parseFloat(getPOLineItemAmount(effectivePoItems[lineItem.id] || [])) >
          lineItem?.availableAmount
      ) {
        isAmtValid = false;
      }
    });

    // PR-linked: block submit until this PR's items are loaded, and while rate-contract
    // statuses are still being resolved for the selected vendor.
    const prItemsNotReady =
      prId && (prItemFilterLoading || Object.keys(prVisiblePoItems).length === 0);

    // PR-linked: block submit while any line item has no active vendor mapping (rate
    // contract) for the selected vendor -- prVisiblePoItems flags these rateContractMissing
    // (see its memo above). The only allowed action on such a row is delete
    // (onDeletePrRow); once removed, this recomputes to false and submit re-enables.
    const hasUnmappedPrItem = prId && hasUnmappedPrItems(prVisiblePoItems);

    return (
      !formDetails.vendor ||
      !formDetails.agreement ||
      !formDetails.deliveryLocation ||
      !formDetails.billingLocation ||
      !isAmtValid ||
      prItemsNotReady ||
      hasUnmappedPrItem ||
      rateContractRevalidating ||
      hasIncompleteChargeRow(extraCharges)
    );
  };

  const poSubtotalBeforeTax =
    Number(getPOAmountWithoutTax(prId ? prVisiblePoItems : poItems)) || 0;

  return (
    <div className="finance-v2-create-po-container">
      <div className="po-create-header">
        <div className="left-container">
          <div className="back-title">
            <IconButton
              onClick={() => {
                navigate(-1);
                // navigate("/finance-v2?index=1");
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography className="title">Create PO</Typography>
          </div>
          <div className="info-section">
            <div className="info-box">
              <span>Site Name </span>
              <span>{displaySiteName || ""}</span>
            </div>
            <div className="info-box">
              <span>Site Code </span>
              <span>{displaySiteCode || ""}</span>
            </div>
            <div className="info-box">
              <span>PO Type </span>
              <span>{convertToCamelCaseFromUnderScore(formDetails.poType || poType)}</span>
            </div>
            <div className="info-box">
              <span>PO Amount </span>
              <span>
                {(
                  Number(getPOAmount(prId ? prVisiblePoItems : poItems)) +
                  getExtraChargesTotal(extraCharges, poSubtotalBeforeTax)
                ).toFixed(2)}
              </span>
            </div>
            <div className="info-box">
              <span>PO Date </span>
              <span>{formatDate(new Date(), DATE_FORMATS["DD-MM-YYYY"])}</span>
            </div>
            <div className="info-box">
              <span>Available Budget Amt </span>
              <span>
                {prId
                  ? availableBudgetAmount(budgetLineItems)
                  : createPOavailableBudgetAmount(lineItems, selectedLineItemIds)}
              </span>
            </div>
            {/* <div className='info-box'>
              <span>Base Amt</span>
              <span>{getPOAmountWithoutTax(poItems)}</span>
            </div>
            <div className='info-box'>
              <span>Tax Amt </span>
              <span>{getPOTaxAmount(poItems)}</span>
            </div>
            <div className='info-box'>
              <span>Total Amt </span>
              <span>{getPOAmount(poItems)}</span>
            </div> */}
          </div>
        </div>

        <div className="right-container">
          <Button
            variant="outlined"
            disabled={isDisabled()}
            onClick={() => {
              onSave();
            }}
          >
            Save as draft
          </Button>
          <Button
            variant="contained"
            disabled={isDisabled()}
            onClick={() => {
              onSave({ createApproval: true });
            }}
          >
            Create order
          </Button>
        </div>
      </div>
      <div className="form-container">
        <div className="form-row">
          <Autocomplete
            onChange={(e, val) => {
              onDropDownChange("company", val);
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Company" />
            )}
            inputValue={autocompleteInputValues.company || ""}
            onInputChange={(event, newInputValue) => {
              if (event) {
                setAutocompleteInputValues({
                  ...autocompleteInputValues,
                  company: newInputValue,
                });
              }
            }}
            getOptionLabel={(op) => op.name || ""}
            options={companyList}
          />
          <Autocomplete
            disabled={isStoreRole() && isPRLinked}
            value={formDetails.vendor?.code ? formDetails.vendor : null}
            onChange={(e, val) => {
              if (isPRLinked) {
                handlePrVendorSelect(val);
              } else {
                onDropDownChange("vendor", val);
              }
            }}
            getOptionLabel={(op) => `${op.code} - ${op.name}`}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Vendor *" />
            )}
            options={isStoreRole() && isPRLinked ? prVendorOptions : vendors}
          />
          <TextField
            value={vendor?.gst || ""}
            disabled
            size="small"
            label="Vendor GST No"
            fullWidth
          />
          <TextField
            value={vendor?.address || ""}
            disabled
            size="small"
            label="Vendor Address"
            fullWidth
          />

          <Autocomplete
            onChange={(e, val) => {
              onDropDownChange("agreement", val);
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Agreement *" />
            )}
            options={["Yes", "No"]}
          />
        </div>
        <div className="form-row">
          <TextField
            value={formDetails.remarks || ""}
            onChange={inputChange}
            size="small"
            name="remarks"
            label="Agreement Remarks"
            fullWidth
          />

          <Autocomplete
            disabled={isDeliveryLocationLocked}
            onChange={(e, val) => {
              onDropDownChange("deliveryLocation", val);
            }}
            value={formDetails.deliveryLocation || ""}
            inputValue={autocompleteInputValues.deliveryLocation || ""}
            onInputChange={(event, newInputValue) => {
              if (event) {
                setAutocompleteInputValues({
                  ...autocompleteInputValues,
                  deliveryLocation: newInputValue,
                });
              }
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Delivery Location *" />
            )}
            getOptionLabel={(op) => op.restaurantName || ""}
            options={getDeliveryAddressList()}
          />
          <Autocomplete
            onChange={(e, val) => {
              onDropDownChange("billingLocation", val);
            }}
            value={formDetails.billingLocation || ""}
            inputValue={autocompleteInputValues.billingLocation || ""}
            onInputChange={(event, newInputValue) => {
              if (event) {
                setAutocompleteInputValues({
                  ...autocompleteInputValues,
                  billingLocation: newInputValue,
                });
              }
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Billing Location *" />
            )}
            getOptionLabel={(op) => op.restaurantName || ""}
            options={getAddressList()}
          />
          <TextField
            value={formDetails.paymentTerms || ""}
            onChange={inputChange}
            size="small"
            name="paymentTerms"
            label="Payment terms(Master vendor)"
            fullWidth
          />

          {isStoreRole() ? (
            <TextField size="small" label="Department" value="Store" disabled />
          ) : (
            <Autocomplete
              onChange={(e, val) => {
                onDropDownChange("department", val);
              }}
              disabled
              getOptionLabel={(op) => op.name}
              value={formDetails.department}
              inputValue={
                (formDetails.department && formDetails.department.name) || ""
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="Department" />
              )}
              options={departmentList}
            />
          )}
        </div>
        <div className="form-row">
          <TextField
            value={formDetails.advancePercent}
            onChange={inputChange}
            size="small"
            type="number"
            name="advancePercent"
            label="Advance Percent"
            fullWidth
          />
          <TextField
            value={
              getAdvanceAmount(
                getPOAmountWithoutTax(prId ? prVisiblePoItems : poItems),
                formDetails.advancePercent || 0
              ) || ""
            }
            onChange={inputChange}
            size="small"
            type="number"
            name="advanceAmount"
            label="Advance Amount"
            disabled
            fullWidth
          />
          <Autocomplete
            onChange={(e, val) => {
              onDropDownChange("currency", val);
            }}
            value={formDetails?.currency || null}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Currency" />
            )}
            options={currencyOptions}
          />
          <TextField
            value={formDetails.termsAndConditions || ""}
            onChange={inputChange}
            size="small"
            name="termsAndConditions"
            label="Terms and conditions"
            fullWidth
          />

          <TextField
            value={formDetails.forex || ""}
            onChange={inputChange}
            type="number"
            size="small"
            name="forex"
            label="Forex Rate"
            fullWidth
          />
        </div>
      </div>
      {(isPRLinked ? formDetails.deliveryLocation && vendor?.code : vendor && vendor.code) ? (
        <>
          <div className="po-additional-attachment-container">
            <FileUpload
              uploadText="Additional Documents"
              clsName="add-additional-doc"
              id="additional-documents"
              url={`${FINANCE_V2_HOSTNAME}${
                FINANCE_REST_URLS.UPLOAD_DOCUMENT
              }${getUploadAttachmentQueryParams(
                { attachmentKey: "documentLinks", budgetId },
                uploadAttachmentTypes.createPo
              )}`}
              callBack={(response) => {
                const { data } = response;
                setAttachments([...attachments, data]);
              }}
              formDataKey="file"
            />
            {attachments.map((attachment, index) => {
              return (
                <Chip
                  label={attachment.originalName}
                  onDelete={() => onDelete(index)}
                  onClick={() => window.open(attachment?.docUrl, "_blank")}
                />
              );
            })}
          </div>
          {prId ? (
            prItemFilterLoading ? (
              <div className="po-attachment-form-container">
                <span>Loading items available from the selected vendor...</span>
              </div>
            ) : Object.keys(prVisiblePoItems).length === 0 ? (
              <div className="po-attachment-form-container">
                <span>
                  This Purchase Requisition has no items to add to a PO.
                </span>
              </div>
            ) : (
            <div className="finance-v2-po-add-items-container">
              <div className="added-po-items-list">
                {Object.entries(prVisiblePoItems).map(([liId, liPoItems]) => {
                  const firstItem = liPoItems[0] || {};
                  const matchedBudgetLineItem = budgetLineItems.find(
                    (li) => String(li.id) === String(liId)
                  );
                  const syntheticLineItem = {
                    id: liId,
                    name: firstItem.lineItemName || liId,
                    availableAmount: matchedBudgetLineItem?.availableAmount ?? Infinity,
                    status: matchedBudgetLineItem?.status || "approved",
                  };
                  return (
                    <Accordion key={liId} defaultExpanded={true}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        className="po-accordion-summary"
                      >
                        <div className="accordion-header">
                          <div className="left-container">
                            <Typography>
                              {firstItem.lineItemName || liId} (
                              {liPoItems.length})
                            </Typography>
                          </div>
                          <div className="right-container">
                            <span>
                              Remaining Qty :{" "}
                              {liPoItems.reduce(
                                (sum, item) =>
                                  sum + Number(item.remainingQuantity || 0),
                                0
                              )}
                            </span>
                            <span>
                              PO Quantity :{" "}
                              {liPoItems.reduce(
                                (sum, item) =>
                                  sum + Number(item.quantity || 0),
                                0
                              )}
                            </span>
                          </div>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        {liPoItems.map((poItem) => {
                          return (
                          <AddItemModal
                            key={`${poItem.id}-${formDetails.vendor?.code || ""}`}
                            poItem={poItem}
                            lineItem={syntheticLineItem}
                            vendorCode={
                              formDetails.vendor && formDetails.vendor.code
                            }
                            isRateContractEnforced={
                              !!formDetails?.department?.isRateContractEnforced
                            }
                            isItemDeleted={config.isItemDeleted}
                            updateItem={(item) =>
                              updatePrItem(item, liId, poItem.id)
                            }
                            isItemsUploaded={false}
                            onDelete={() => onDeletePrRow(liId, poItem.id)}
                            prQuantity={poItem.remainingQuantity}
                            isPRLinked={isPRLinked}
                            subcategoryList={
                              subcategoryMap[String(poItem.category?.id ?? "")] || []
                            }
                            poDate={formDetails.poDate}
                            rateContractMissing={!!poItem.rateContractMissing}
                          />
                        );
                      })}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
                <ExtraChargesSection
                  charges={extraCharges}
                  onChangeCharges={setExtraCharges}
                  poSubtotalBeforeTax={poSubtotalBeforeTax}
                />
              </div>
            </div>
            )
          ) : (
            <>
              {!isItemsUploaded && (
                <div className="po-attachment-form-container">
                  <FileUpload
                    uploadText="Upload PO Item Details"
                    clsName="add-additional-doc"
                    id="po-items-excel"
                    url={`${FINANCE_V2_HOSTNAME}${
                      FINANCE_REST_URLS.UPLOAD_MASTER_DATA
                    }po-item-details?site=${site}&lineItemIds=${lineItemIds}&vendorCode=${
                      (vendor && vendor.code) || ""
                    }`}
                    callBack={onLineItemsUpload}
                    formDataKey="file"
                  />
                  <DownloadTemplateButton type={uploadTemplates["po-items"]} />
                </div>
              )}
              <div className="finance-v2-po-add-items-container">
                <div className="added-po-items-list">
                  {lineItems.map((lineItem) => {
                    if (!selectedLineItemIds.includes(lineItem.id)) {
                      return;
                    }
                    return (
                      <Accordion defaultExpanded={true}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          className="po-accordion-summary"
                        >
                          <div className="accordion-header">
                            <div className="left-container">
                              <Typography>
                                {lineItem.name} (
                                {(poItems[lineItem.id] || []).length})
                              </Typography>

                              {formDetails.vendor && formDetails.vendor.id && (
                                <IconButton
                                  disabled={
                                    lineItem.availableAmount <= 0 ||
                                    !(
                                      (formDetails.vendor &&
                                        formDetails.vendor.id) ||
                                      ""
                                    )
                                  }
                                  onClick={(event) => {
                                    onRowAdd(lineItem);
                                    event.stopPropagation();
                                  }}
                                >
                                  <AddCircle color="primary" />
                                </IconButton>
                              )}
                            </div>
                            <div className="right-container">
                              <span>
                                Available Amount :{" "}
                                {Math.round(lineItem.availableAmount).toFixed(2)}
                              </span>
                              <span
                                className={
                                  getPOLineItemAmount(
                                    poItems[lineItem.id] || []
                                  ) > lineItem.availableAmount
                                    ? "error"
                                    : ""
                                }
                              >
                                Order Amount :{" "}
                                {getPOLineItemAmount(poItems[lineItem.id] || [])}
                              </span>
                            </div>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails>
                          {(poItems[lineItem.id] || []).map((poItem, index) => {
                            return (
                              <AddItemModal
                                poItem={poItem}
                                lineItem={lineItem}
                                vendorCode={
                                  formDetails.vendor && formDetails.vendor.code
                                }
                                isRateContractEnforced={
                                  !!formDetails?.department
                                    ?.isRateContractEnforced
                                }
                                isItemDeleted={config.isItemDeleted}
                                updateItem={(item) => updateItem(item, index)}
                                isItemsUploaded={isItemsUploaded}
                                onDelete={() => onDeleteRow(lineItem.id, index)}
                                isPRLinked={false}
                                subcategoryList={
                                  subcategoryMap[String(lineItem.category?.id ?? "")] || []
                                }
                                poDate={formDetails.poDate}
                              />
                            );
                          })}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                  <ExtraChargesSection
                    charges={extraCharges}
                    onChangeCharges={setExtraCharges}
                    poSubtotalBeforeTax={poSubtotalBeforeTax}
                  />
                </div>
              </div>
            </>
          )}
        </>
      ) : prId ? (
        <div className="pr-vendor-step">
          <Typography variant="subtitle1">
            Select Delivery Location and Vendor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isDeliveryLocationLocked
              ? "Delivery Location is fixed to this Purchase Requisition's store. Choose a vendor to see the items they can supply."
              : formDetails.deliveryLocation
              ? "Choose a vendor to see the items from this store's allocation that they can supply."
              : "Choose the store to deliver to"}
          </Typography>
          <Autocomplete
            disabled={isDeliveryLocationLocked}
            value={formDetails.deliveryLocation || null}
            onChange={(e, val) => {
              onDropDownChange("deliveryLocation", val);
              setAutocompleteInputValues((prev) => ({
                ...prev,
                deliveryLocation: val?.restaurantName || "",
              }));
            }}
            inputValue={autocompleteInputValues.deliveryLocation || ""}
            onInputChange={(event, newInputValue) => {
              if (event) {
                setAutocompleteInputValues((prev) => ({
                  ...prev,
                  deliveryLocation: newInputValue,
                }));
              }
            }}
            getOptionLabel={(op) => op.restaurantName || ""}
            options={getDeliveryAddressList()}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Delivery Location *" />
            )}
            sx={{ width: 400 }}
          />
          <Autocomplete
            disabled={!formDetails.deliveryLocation}
            loading={prVendorsLoading}
            options={prVendorOptions}
            onChange={(e, val) => {
              if (val) handlePrVendorSelect(val);
            }}
            getOptionLabel={(op) => `${op.code} - ${op.name}`}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Vendor *" />
            )}
            sx={{ width: 400 }}
          />
        </div>
      ) : (
        <div className="po-attachment-form-container">
          <span>Please select vendor to add items</span>
        </div>
      )}
      {createdPoDetails && (
        <CustomModal
          title="PO Successfully created"
          disabledOutsideClick={true}
        >
          <POInfo
            poDetails={createdPoDetails}
            siteCode={displaySiteCode}
            siteName={displaySiteName}
            onOk={() => {
              navigate(
                getHomeTabUrl(
                  HOME_TABS.PO,
                  getPoListSubIndex(createdPoDetails?.status)
                )
              );
            }}
          />
        </CustomModal>
      )}
    </div>
  );
};

export default CreatePO;
